import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import {Grid, Typography, Link, Box} from "@material-ui/core";
import {useBreakpoints} from "../../../hooks";
import PropTypes from "prop-types";
import clsx from "clsx";
import * as ROUTES from "../../../common/routes";
import {useI18N, useLocalizedUrl, useLocation} from "../../../common/common";
import {RESORTS_BOOKING_INFORMATION} from "../../../common/routes";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";

const useStyles = makeStyles(theme => ({
    footerContainer: {
        bottom: 0,
        padding: theme.spacing(10, 5, 5, 5),
        background: theme.palette.secondary["50"],
        [theme.breakpoints.down("xs")]: {
            padding: theme.spacing(3, 2, 4, 2)
        },
    },
    footer: {
        maxWidth: "1600px"
    },
    heading: {
        color: theme.palette.grey["800"],
        textTransform: "uppercase",
        paddingBottom: theme.spacing(2)
    },
    onHoverUnderline: {
        "&:hover": {
            textDecorationLine: "underline",
            cursor: "pointer"
        }
    },
    onHoverNoUnderline: {
        "&:hover": {
            textDecorationLine: "none",
        }
    },
    img: {
        background: theme.palette.secondary["100"],
        width: theme.spacing(4),
        height: theme.spacing(4),
        borderRadius: theme.spacing(0.5),
        "&:hover": {
            background: "rgba(232, 230, 227, 0.14)",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2), 0px 1px 10px rgba(0, 0, 0, 0.12), 0px 4px 5px rgba(0, 0, 0, 0.14)",
            border: "1px solid #D8D5D0",
            boxSizing: "border-box"
        }
    },
    buttonMarginTop: {
        marginTop: theme.spacing(7),
        [theme.breakpoints.down("md")]: {
            marginTop: theme.spacing(1)
        },
        [theme.breakpoints.down("xs")]: {
            marginTop: theme.spacing(4)
        },
    },
    textInfo: {
        color: theme.palette.text.medium,
    },
    mb2: {
        marginBottom: theme.spacing(2)
    },
    capitalize: {
        textTransform: "capitalize"
    },
    mb1point5: {
        marginBottom: theme.spacing(1.5)
    },
    displayNone: {
        display: "none"
    },
    noWrap: {
        [theme.breakpoints.up("md")]: {
            flexWrap: "nowrap"
        }
    },
    socialIcon: {
        width: theme.spacing(4),
        height: theme.spacing(4),
    }
}));

const FooterTop = ({companyDetails, footerCollection}) => {
    const classes = useStyles();
    const {isRouteInLocation} = useLocation();
    const {isExtraSmallScreen} = useBreakpoints();
    const {getLocalizedUrl} = useLocalizedUrl();
    const {i18n} = useI18N();
    const {sendCustomEvent, eventNames} = useAnalytics();

    const getUrl = (linkItems) => {
        if (linkItems.url) {
            return linkItems.url;
        } else if (linkItems.contentPage) {
            return getLocalizedUrl("/" + linkItems.contentPage.slug);
        } else {
            return getLocalizedUrl(linkItems.internalUrl);
        }
    };

    const getLinkItems = (index, linkItems, sectionTitle) => {
        return (
            <Box
                key={index}
                className={classes.mb1point5}
            >
                <Link
                    href={getUrl(linkItems)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body1"
                    className={classes.textInfo}
                    onClick={() => handleGA4NavigationTracking(`${sectionTitle} / ${linkItems.label_en_gb}`)}
                >
                    {linkItems.label}
                </Link>
            </Box>
        );
    };

    const handleGA4NavigationTracking = (clickItem) => {
        sendCustomEvent({
            event_name: eventNames.NAVIGATION,
            [eventNames.NAVIGATION]: {
                click_item: clickItem,
                navigation_type: "footer"
            }
        });
    };

    const socialMedia = {
        INSTAGRAM: {
            link: "https://www.instagram.com/hapimag_resorts/?hl=en",
            imageUrl: "/images/Instagram.svg",
            imageAlt: "instagram-icon",
            isExternal: true
        },
        FACEBOOK: {
            link: "https://www.facebook.com/hapimag/",
            imageUrl: "/images/Facebook.svg",
            imageAlt: "facebook-icon",
            isExternal: true
        },
        YOUTUBE: {
            link: "https://www.youtube.com/c/hapimag",
            imageUrl: "/images/Youtube.svg",
            imageAlt: "youtube-icon",
            isExternal: true
        },
        LINKEDIN: {
            link: "https://www.linkedin.com/company/hapimag/",
            imageUrl: "/images/Linkedin.svg",
            imageAlt: "linkedin-icon",
            isExternal: true
        },
    };



    return (
        <Grid
            container
            justify="center"
            className={clsx(classes.footerContainer, {[classes.displayNone]: isRouteInLocation(ROUTES.ONLINE_ACCESS)})}
        >
            <Grid
                container
                direction="row"
                justify="space-between"
                className={classes.footer}
                spacing={isExtraSmallScreen() ? 4 : 3}
            >
                {footerCollection?.map((footerItem, index) => {
                    return (
                        <Grid
                            key={index}
                            item
                            lg
                            md={3}
                            sm={6}
                            xs={12}
                            className={classes.mt}
                        >
                            <Grid
                                container
                                direction="row"
                            >
                                <Grid item>
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            component="div"
                                            className={classes.heading}
                                        >
                                            {footerItem.title}
                                        </Typography>
                                    </Box>

                                    <Box
                                        display={"flex"}
                                        flexDirection={"column"}
                                    >
                                        {footerItem?.linkedObjectsCollection?.items.map((linkItems, index) => {
                                            return getLinkItems(index, linkItems, footerItem.title_en_gb);
                                        })}
                                        {index === footerCollection?.length - 1 &&
                                            <>
                                                <Box
                                                    className={classes.mb1point5}
                                                >
                                                    <Typography
                                                        onClick={() => window.OneTrust.ToggleInfoDisplay()}
                                                        variant="body1"
                                                        className={clsx(classes.textInfo, classes.onHoverUnderline)}
                                                        onKeyDown={(event) => {
                                                            if (event.keyCode === 13 || event.keyCode === 32) {
                                                                event.preventDefault();
                                                                event.target.click();
                                                            }
                                                        }}
                                                        tabIndex={0}
                                                    >
                                                        {i18n("layout.footer.cookie_settings")}
                                                    </Typography>
                                                </Box>

                                                {companyDetails && (
                                                    <Box
                                                        className={classes.mb1point5}
                                                    >
                                                        <Link
                                                            href={getLocalizedUrl(RESORTS_BOOKING_INFORMATION)}
                                                            target={"_self"}
                                                            rel="noopener noreferrer"
                                                            variant="body1"
                                                            className={classes.textInfo}
                                                        >
                                                            {i18n("layout.footer.booking_info")}
                                                        </Link>
                                                    </Box>
                                                )}

                                                <Grid
                                                    container
                                                    direction="row"
                                                    spacing={2}
                                                    className={classes.noWrap}
                                                >
                                                    {Object.entries(socialMedia).map(([key, value]) => (
                                                        <Grid
                                                            key={key}
                                                            item
                                                        >
                                                            <Link
                                                                href={value.link}
                                                                target="_blank"
                                                                {...(value.isExternal && {rel: "noopener noreferrer"})}
                                                            >
                                                                <img
                                                                    src={value.imageUrl}
                                                                    alt={value.imageAlt}
                                                                    className={classes.socialIcon}
                                                                />
                                                            </Link>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </>
                                        }
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    );
                })}
            </Grid>
        </Grid>
    );
};

FooterTop.propTypes = {
    footerCollection: PropTypes.array,
    companyDetails: PropTypes.object
};

export default FooterTop;
